import {
  type RailMenuEntry,
  type RailMenuItem,
  type RailRow,
  type RailRowActionId,
  railRowMenu,
} from '@modules/common/components/rail-menu/rail-menu';
import { type VirtualFolder } from '@modules/contacts/types/virtual-folder';

/** Whose Contacts rail row a menu belongs to: the member's My Workspace or the Team Workspace. */
export type ContactsRailScope = 'mine' | 'team';

/** A Contacts rail row's key: the app's virtual folders plus the Shared header, which is not a folder. */
export type ContactsRailKey = VirtualFolder | 'shared';

/** Contacts' rail rows by the prototype's names. Rows not listed here keep their own menu. */
export const CONTACTS_RAIL_ROWS: Readonly<Partial<Record<ContactsRailKey, RailRow>>> = {
  all_contacts: 'all',
  all_groups: 'all',
  vip: 'vip',
  shared: 'shared',
  flagged: 'flagged',
  followed: 'followup',
  snoozed: 'snoozed',
  following: 'following',
  archived: 'archive',
  deleted: 'trash',
};

const TEAM_CLEAR_HINTS = {
  flagged: 'This removes flags for everyone in the team.',
  followed: 'This clears your follow-up reminders in this view. Other teammates’ reminders are unchanged.',
  snoozed: 'This clears your snoozes in this view. Other teammates’ reminders are unchanged.',
} as const;

const TEAM_CLEAR_TITLES = {
  flagged: 'Unflag all',
  followed: 'Remove all follow-ups',
  snoozed: 'Unsnooze all',
} as const;

export const PINS_SHARED_HINT = 'Pins in this workspace are shared by the team.';

/**
 * A Contacts workspace rail row's menu: the prototype's entries from the shared definition, plus Contacts' own
 * entries kept beside them (additive rule). `undefined` for a row the shared definition does not cover.
 *
 * Every destructive bulk entry is Coming soon except the My Workspace "Empty trash", which keeps today's
 * confirmed purge: none of the others has a confirmation-and-captured-ids flow in Contacts yet.
 */
export function contactsRowMenu(key: ContactsRailKey, scope: ContactsRailScope): readonly RailMenuItem[] | undefined {
  const row = CONTACTS_RAIL_ROWS[key];
  if (!row) return undefined;
  const team = scope === 'team';

  const live = new Set<RailRowActionId>(['new-item', 'new-container', 'settings']);
  if (team) {
    // Team flags are one shared state: the personal "Clear my flags" has nothing of its own to clear (Coming soon).
    if (key === 'followed' || key === 'snoozed' || key === 'following') live.add('clear');
  } else if (key === 'flagged' || key === 'followed' || key === 'snoozed') {
    live.add('clear');
  }

  const extras: RailMenuEntry[] = [];
  if (team && (key === 'flagged' || key === 'followed' || key === 'snoozed')) {
    extras.push({
      id: 'clear-team',
      title: TEAM_CLEAR_TITLES[key],
      icon: 'sapphire-close',
      hint: TEAM_CLEAR_HINTS[key],
    });
  }
  // My Following has no "Settings" in the old menu either; the team's Following always had one.
  if (key === 'archived') {
    extras.push({ id: 'delete-all', title: 'Delete all', icon: 'sapphire-trash', dangerous: true, comingSoon: true });
  }
  if (key === 'deleted') {
    extras.push({
      id: 'empty-trash-all',
      title: 'Empty trash',
      icon: 'sapphire-trash',
      dangerous: true,
      comingSoon: team,
    });
  }

  return railRowMenu({
    row,
    itemNoun: 'contact',
    containerNoun: 'contact group',
    readable: false,
    live,
    extras,
  });
}

/**
 * The menu of the Pinned/Team Knots or Tags row. The prototype's own entry manages the member's personal
 * pins, which a team's shared pins cannot be (Coming soon); Contacts' Add and Manage stay beside it.
 */
export function contactsPinnedMenu(kind: 'knots' | 'tags', scope: ContactsRailScope): readonly RailMenuItem[] {
  const team = scope === 'team';
  return [
    { id: 'manage-pins', title: `Manage my pinned ${kind}`, icon: 'sapphire-settings', comingSoon: team },
    null,
    { id: 'add-meta', title: `Add ${kind}`, icon: 'sapphire-plus' },
    {
      id: 'manage-meta',
      title: `Manage ${kind}`,
      icon: 'sapphire-settings',
      hint: team ? PINS_SHARED_HINT : undefined,
    },
  ];
}
